import { useEffect, useMemo, useRef, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { prepacksApi } from '@/services/api'
import { usePrepackPreview } from './prepackPreviewContext'
import { getTemplateVariant } from '../../../../../shared/pkg/prepacks/templateVariants.js'
import { RESTYLE_PICTURE, restyleIncompatibility } from '../../../../../shared/pkg/prepacks/templateRestyle.js'
import { canonicalAiLeg } from './prepackCardModel'

const NO_TEAM_COLORS_NOTICE = 'No team colors found for this pack. Team colors cover NFL, college football (FBS), NBA, WNBA, MLB and NHL teams.'

// Compares variable maps whatever order the parent rebuilds their keys in.
const varsKey = (vars) => JSON.stringify(Object.keys(vars).sort().map((key) => [key, vars[key]]))

// `nativeColors` are the Default card's plain colors as its pickers show them;
// that card has no template color variables of its own.
export default function PrepackStyleAction({ orgSlug, template, pack, nativeColors, disabled = false, onChange }) {
  const { branding } = usePrepackPreview()
  const colorMode = branding?.darkMode ? 'dark' : 'light'
  const variant = getTemplateVariant(template, colorMode)
  const native = Boolean(template?.isFallback)
  const incompatibility = native ? null : restyleIncompatibility(template)
  const hasColors = native
    ? Object.keys(nativeColors || {}).length > 0
    : variant.cssVars.some((variable) => variable.type === 'color')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [previous, setPrevious] = useState(null)
  const [proposal, setProposal] = useState(null)
  const mounted = useRef(true)
  const requestId = useRef(0)
  const controller = useRef(null)
  const templateVars = pack?.template_vars && typeof pack.template_vars === 'object' ? pack.template_vars : {}
  const templateVarsKey = varsKey(templateVars)
  // The colors "Use colors" handed to the parent. While the pack still has
  // exactly these, the change is this action's own and Undo stays.
  const applied = useRef(null)
  // Everything the request is built from. A change to any of it invalidates
  // an in-flight proposal, so colours drawn for one subject never land on another.
  const subjectLegs = useMemo(() => (Array.isArray(pack?.legs) ? pack.legs.map(canonicalAiLeg) : []), [pack?.legs])
  const identity = `${orgSlug || ''}:${template?.id || 'native'}:${colorMode}:${pack?.id || 'preview'}:${pack?.title || ''}:${pack?.regulation || ''}:${pack?.sport || ''}:${pack?.league || ''}:${templateVarsKey}:${JSON.stringify(subjectLegs)}:${JSON.stringify(nativeColors ?? null)}`

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      controller.current?.abort()
    }
  }, [])

  useEffect(() => {
    requestId.current += 1
    controller.current?.abort()
    controller.current = null
    setBusy(false)
    setError('')
    setNotice('')
    setProposal(null)
    if (applied.current !== templateVarsKey) {
      applied.current = null
      setPrevious(null)
    }
  }, [identity, templateVarsKey])
  useEffect(() => {
    if (!disabled) return
    requestId.current += 1
    controller.current?.abort()
    controller.current = null
    setBusy(false)
  }, [disabled])

  const restyle = async () => {
    if (!orgSlug || !template?.id || busy || disabled) return
    const currentRequest = ++requestId.current
    const requestController = new AbortController()
    controller.current?.abort()
    controller.current = requestController
    const before = templateVars
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const result = await prepacksApi.restylePrepack(orgSlug, {
        title: pack?.title,
        template_id: template.id,
        // The Default card sends its current plain colors, since the API
        // cannot resolve the branding they inherit; artwork sends overrides.
        template_vars: native ? nativeColors : before,
        color_mode: colorMode,
        // The API refreshes each leg's outcome in this regulation to learn a
        // player's team; the legs' event and outcome ids make that possible.
        regulation: pack?.regulation,
        sport: pack?.sport,
        league: pack?.league,
        legs: subjectLegs,
      }, { signal: requestController.signal })
      if (!mounted.current || requestId.current !== currentRequest || requestController.signal.aborted) return
      if (result?.no_team_colors) {
        setNotice(NO_TEAM_COLORS_NOTICE)
        setProposal(null)
        return
      }
      const variables = result?.variables && typeof result.variables === 'object' ? result.variables : {}
      if (Object.keys(variables).length === 0) {
        setError('No color suggestion returned.')
      } else {
        setProposal(variables)
      }
    } catch (failure) {
      if (mounted.current && requestId.current === currentRequest && !requestController.signal.aborted) {
        const data = failure?.response?.data
        setError(data?.error?.message || (typeof data?.error === 'string' ? data.error : '') || 'AI colors could not be generated. Try again.')
      }
    } finally {
      if (controller.current === requestController) controller.current = null
      if (mounted.current && requestId.current === currentRequest) setBusy(false)
    }
  }

  const applyProposal = () => {
    if (disabled || !proposal) return
    const before = pack?.template_vars && typeof pack.template_vars === 'object' ? pack.template_vars : {}
    const next = { ...before, ...proposal }
    setPrevious(before)
    applied.current = varsKey(next)
    onChange?.(next)
    setProposal(null)
  }

  const undo = () => {
    applied.current = null
    onChange?.(previous)
    setPrevious(null)
  }

  return (
    <div className="w-full text-xs">
      {incompatibility ? (
        <p className="text-[var(--prepacks-muted)]">
          {incompatibility === RESTYLE_PICTURE
            ? "AI colors aren't available for templates with a picture."
            : "AI colors aren't available for templates with custom code."}
        </p>
      ) : (
        <>
          <button
            type="button"
            disabled={disabled || busy || !orgSlug || !hasColors}
            onClick={restyle}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[var(--prepacks-border)] px-3 py-2.5 text-[var(--prepacks-accent-text)] hover:border-[var(--prepacks-accent-text)] disabled:opacity-50"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {busy ? 'Restyling…' : 'Match colors to this pack'}
          </button>
          {proposal ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {Object.entries(proposal).map(([key, color]) => (
                <span key={key} title={`${key}: ${color}`} className="h-4 w-4 rounded-full border border-[var(--prepacks-border)]" style={{ backgroundColor: color }} />
              ))}
              <button type="button" disabled={disabled || busy} className="text-[var(--prepacks-accent-text)] disabled:opacity-50" onClick={applyProposal}>Use colors</button>
              <button type="button" disabled={disabled || busy} className="text-[var(--prepacks-muted)] disabled:opacity-50" onClick={() => setProposal(null)}>Discard</button>
            </div>
          ) : null}
          {previous != null ? (
            <button type="button" disabled={disabled || busy} onClick={undo} className="mt-2 text-[var(--prepacks-accent-text)] disabled:opacity-50">Undo AI colors</button>
          ) : null}
          {notice ? <p role="status" className="mt-2 text-[var(--prepacks-muted)]">{notice}</p> : null}
          {error ? <p role="alert" className="mt-2 text-[var(--wf-color-danger)]">{error}</p> : null}
        </>
      )}
    </div>
  )
}
